import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiMessageCircle, FiUsers, FiTarget, FiSunrise, FiPlus, FiMinus } = FiIcons;

const APPLY_URL = "https://api.productivetherapist.com/widget/form/GThGT4LnGisYLV72ZQCh";

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="border-b border-gray-200 py-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left focus:outline-none group"
      >
        <h3 className={`text-xl font-bold transition-colors duration-300 ${isOpen ? 'text-[#1B80C9]' : 'text-[#08213d] group-hover:text-[#1B80C9]'}`}>
          {question}
        </h3>
        <div className={`flex-shrink-0 ml-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#1B80C9] text-white rotate-180' : 'bg-gray-100 text-[#08213d]'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="w-5 h-5" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-4 text-gray-600 text-lg leading-relaxed whitespace-pre-wrap">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ApplyButton = ({ className = "", text = "Apply Now - Only 10 Spots", variant = "primary" }) => {
  const baseStyles = "px-10 py-4 rounded-full font-bold text-lg transition-all shadow-xl flex items-center transform hover:scale-105 active:scale-95 w-fit mx-auto";
  const variants = {
    primary: "bg-gradient-to-r from-[#0EBA8E] to-[#4EB296] text-white hover:from-[#0ca678] hover:to-[#45a085]",
    secondary: "bg-gradient-to-r from-[#1B80C9] to-[#0EBA8E] text-white hover:opacity-90",
    blue: "bg-gradient-to-r from-[#4EB296] to-[#4F8DD0] text-white hover:opacity-90"
  };

  return (
    <a 
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseStyles} ${variants[variant]} ${className}`}
    >
      <SafeIcon icon={FiMessageCircle} className="w-6 h-6 mr-2" /> {text}
    </a>
  );
};

const LandingPage = () => {
  const scheduleItems = [
    {
      month: "Month 1: AI Foundation & Setup",
      timeline: "February",
      description: "Set up your personalized AI system and assemble your AI advisory team. This is the foundation that makes everything else work.",
      walkaway: "24/7 AI advisors helping you make confident business decisions (finance, marketing, operations). Includes your private 45-minute strategy call with Uriah."
    },
    {
      month: "Month 2: AI Receptionist Implementation",
      timeline: "March",
      description: "Complete playbook with Uriah's prompts, setup guides, and best practices for voice + chat AI. Works with any AI tool you choose, but we highly recommend using Claude Pro.",
      walkaway: "AI handling inquiries in seconds not hours, without hiring any more admin staff."
    },
    {
      month: "Month 3: SOPs + Retreat",
      timeline: "April",
      description: "Use AI to create and maintain standard operating procedures so your systems outlive any single team member. Document what works so it keeps working. Includes in-person Northern California wine country retreat (4/23-4/24) with meals and tastings.",
      walkaway: "Battle-tested SOPs ensuring your practice operations run consistently + renewed energy and deep peer connections from the retreat."
    },
    {
      month: "Month 4: Custom AI Solutions",
      timeline: "May",
      description: "Custom AI solutions for YOUR biggest challenge - whether that's marketing automation, clinical documentation, client communication, or practice-specific workflows. This is where AI becomes tailored to what YOU actually need.",
      walkaway: "Hours reclaimed each week from AI workflows built specifically for your practice's unique challenges - marketing, operations, or whatever's holding you back."
    },
    {
      month: "Month 5: AI Leadership & Team Integration",
      timeline: "June",
      description: "Lead your practice through AI transformation. Get your staff working alongside AI effectively with training and documented processes.",
      walkaway: "Your team confidently using AI, fully adopted systems across your practice."
    },
    {
      month: "Month 6: Lock In Your Results",
      timeline: "July",
      description: "Make everything stick. Update your SOPs with lessons learned, create simple maintenance routines so your AI keeps delivering results and build succession plans so your AI systems survive team changes. Graduate with a bulletproof system, not just good intentions.",
      walkaway: "A complete AI system that runs consistently, measurable time and revenue gains, and the confidence that you've built something lasting - not just another tool you'll abandon in 6 months."
    }
  ];

  const differentiators = [
    {
      title: "Small & Intimate",
      icon: FiUsers,
      description: "Limited to just 10 practice owners. You're not another face in a Zoom room. You get personalized attention, a private kickoff call with Uriah, and deep connections with peers who actually get what you're dealing with."
    },
    {
      title: "Implementation Focus",
      icon: FiTarget,
      description: "This isn't theory or another course to finish \"someday.\" Weekly challenges keep you accountable. The community keeps you moving. You'll build real AI systems that work in your practice, not collect more ideas you never use."
    },
    {
      title: "In-Person Experience",
      icon: FiSunrise,
      description: "Wine country retreat with all meals and tastings included. Meet your cohort face-to-face, strengthen relationships beyond Zoom, and return home with renewed energy and clarity for scaling your practice."
    }
  ];

  const whatsIncludedItems = [
    "12 expert-facilitated mastermind calls (2 per month)",
    "Private 45-minute kickoff strategy call with Uriah",
    "Northern California wine country retreat (all meals & tastings included)",
    "Complete AI Receptionist Playbook (prompts + implementation guides)",
    "AI Foundation framework (master prompt + advisory team)",
    "SOP templates for sustainable systems",
    "Easy weekly challenges to help you apply what you're learning (optional, but transformational)",
    "6 months of support in a private community"
  ];

  const faqs = [
    {
      question: "Do I need AI experience to join?",
      answer: "No - just curiosity and willingness to learn.\n\nThis mastermind intentionally brings together people at different experience levels. Beginners benefit from learning alongside people who've been using AI for months. Advanced users get fresh perspectives from people asking questions they hadn't considered.\n\nYou need: Eagerness to try new things, 2-3 hours/week to implement, and openness to learning from the group.\n\nYou don't need: Technical skills, coding knowledge, or previous AI experience. If you're curious about AI but not sure where to start, you're in the right place."
    },
    {
      question: "What if I can't make all the calls?",
      answer: "All calls will be recorded and viewable in the private community any time."
    },
    {
      question: "Do I need to use a specific AI tool or platform?",
      answer: "No. This mastermind is platform-agnostic. You'll get prompts, strategies, and systems that work with whatever tools you choose. Whether you use ChatGPT, Claude, our SimpleIntake platform, or any other AI system, the principles and playbooks apply. With that said, we do strongly recommend using Claude on the Pro plan."
    },
    {
      question: "What's the retreat schedule and location?",
      answer: "The retreat will be held on April 23-24. You can fly into Santa Rosa Airport, which is in the same city as our retreat location or San Francisco Airport, which is about 60 minutes away by car. We will do some co-working in the first part of the day, then have fun with activities in the afternoon and end with dinner at an amazing restaurant. You are responsible for your airfare and accommodations."
    },
    {
      question: "Can I bring a team member to the retreat?",
      answer: "We will work on strategies to engage your whole team in using AI to grow the practice, but the retreat time is just for you."
    },
    {
      question: "What's your return policy?",
      answer: "I want you to feel completely confident in this investment. If after your private kickoff call and the first two group sessions you don't feel this is the right fit, let me know by March 1st and I'll refund 100% of what you've paid - no questions asked.\n\nAfter Month 1, all payments are non-refundable due to the personalized nature of the program, retreat planning, and limited spots in the cohort."
    },
    {
      question: "How much time will this require each week?",
      answer: "Two 60-minute calls per month (on 2nd and 4th Tuesdays, 10-11am PST), plus weekly implementation challenges that take 15-30 minutes. You'll also want time to actually implement what you're learning in your practice, but that's flexible based on your schedule and needs."
    }
  ];

  return (
    <div className="min-h-screen bg-[#08213d] text-white">
      {/* Floating Apply Button */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="fixed top-6 left-6 z-50"
      >
        <a 
          href={APPLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-gradient-to-r from-[#0EBA8E] to-[#4EB296] text-white px-6 py-3 rounded-full font-semibold text-sm hover:from-[#0ca678] hover:to-[#45a085] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center transform hover:scale-105"
        >
          <SafeIcon icon={FiMessageCircle} className="w-4 h-4 mr-2" />
          Apply Now
        </a>
      </motion.div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <svg className="w-full h-full object-cover" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="blueGradient1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4A90E2" /><stop offset="100%" stopColor="#87CEEB" /></linearGradient>
            </defs>
            <circle cx="150" cy="100" r="180" fill="url(#blueGradient1)" opacity="0.6" />
          </svg>
        </div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-[#0EBA8E] text-lg font-semibold mb-8 uppercase tracking-wide">ATTENTION: THERAPISTS READY TO SCALE WITH AI IN 2026</p>
            
            <div className="flex justify-center mb-8">
              <img 
                src="https://i.imgur.com/ZpYspYl.png" 
                alt="Uriah's AI Mastermind" 
                className="max-w-full md:max-w-2xl h-auto rounded-3xl shadow-2xl"
              />
            </div>

            <p className="text-2xl md:text-3xl text-[#1B80C9] font-semibold mb-6">A 6-Month Mastermind for Group Practice Owners</p>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-10">
              Taking you from overwhelmed owner to confident leader
            </p>

            <ApplyButton />
          </motion.div>
        </div>
      </section>

      {/* Crossroads Section */}
      <section className="bg-white text-[#08213d] py-24 relative z-20 shadow-inner">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-16">You're at a crossroads.</h2>
            <div className="grid md:grid-cols-2 gap-12 items-stretch">
              <div className="p-8 md:p-10 rounded-3xl bg-gray-50 border border-gray-100 flex flex-col items-start text-left">
                <h3 className="text-xl font-bold mb-6 text-gray-500 uppercase tracking-widest">Path 1: Keep doing what you're doing</h3>
                <ul className="space-y-4 text-lg md:text-xl text-gray-600 font-light">
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Manage every detail.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Answer every question.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Stay indispensable.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Burn out slowly.</span>
                  </li>
                </ul>
              </div>
              <div className="p-8 md:p-10 rounded-3xl bg-[#e3f1fc] border border-[#1B80C9]/10 flex flex-col items-start text-left">
                <h3 className="text-xl font-bold mb-6 text-[#1B80C9] uppercase tracking-widest">PATH 2: BUILD AI SYSTEMS THAT WORK WHILE YOU LEAD</h3>
                <ul className="space-y-4 text-lg md:text-xl text-gray-700">
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Make decisions with confidence.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Scale without adding chaos.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EBA8E] mr-3 mt-1 font-bold leading-none select-none">•</span>
                    <span>Finally work ON your practice instead of IN it.</span>
                  </li>
                </ul>
              </div>
            </div>
            <motion.p initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.5 }} viewport={{ once: true }} className="mt-16 text-2xl md:text-3xl font-bold text-[#0EBA8E]">This mastermind is Path 2.</motion.p>
            
            <motion.div 
              initial={{ opacity: 0 }} 
              whileInView={{ opacity: 1 }} 
              transition={{ delay: 0.6, duration: 0.8 }} 
              viewport={{ once: true }}
              className="mt-8 max-w-3xl mx-auto"
            >
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-12">
                AI is already changing how practices operate. <br />
                The question isn't whether this is happening. It's whether you'll be prepared or be playing catch-up.
              </p>

              <ApplyButton variant="secondary" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Personal Message Video Section */}
      <section className="bg-[#08213d] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <circle cx="700" cy="100" r="150" fill="#1B80C9" />
          <circle cx="100" cy="400" r="120" fill="#0EBA8E" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
              <h2 className="text-2xl font-bold mb-6 text-white">A Personal Invitation From Uriah</h2>
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <iframe 
                  src="https://www.loom.com/embed/9dffd7902ffc433e9202118bea91078c?speed=1" 
                  frameBorder="0" 
                  allowFullScreen 
                  className="w-full h-64 md:h-96"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Schedule Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-[#08213d]">What We'll Build Together</h2>
            </div>
            <div className="space-y-8 mb-16">
              {scheduleItems.map((item, idx) => (
                <div key={idx} className="bg-gray-50 rounded-3xl overflow-hidden border border-gray-200 flex flex-col md:flex-row shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="w-full md:w-4 bg-[#1B80C9] shrink-0"></div>
                  <div className="p-8 md:p-10 flex-grow">
                    <div className="flex flex-wrap justify-between items-start mb-6 gap-4">
                      <h3 className="text-2xl md:text-3xl font-bold text-[#1B80C9]">{item.month}</h3>
                      <span className="px-4 py-1 bg-[#e3f1fc] text-[#1B80C9] rounded-full font-bold uppercase tracking-wider text-sm">{item.timeline}</span>
                    </div>
                    <p className="text-xl text-[#08213d] leading-relaxed mb-6 font-medium">{item.description}</p>
                    <div className="bg-white p-6 rounded-2xl border border-[#0EBA8E]/20">
                      <div className="flex items-start">
                        <SafeIcon icon={FiCheck} className="text-[#0EBA8E] w-5 h-5 mr-3 mt-1 shrink-0" />
                        <div>
                          <p className="text-[#0EBA8E] font-bold text-lg mb-1 leading-none">You'll walk away with:</p>
                          <p className="text-gray-700 text-lg leading-relaxed">{item.walkaway}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ApplyButton variant="blue" />
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-[#e3f1fc] py-20 text-[#08213d]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-center">What Practice Owners Are Saying</h2>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }} 
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-[#08213d]/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 text-center border border-[#08213d]/10">
              <h3 className="text-xl font-bold mb-6 text-[#08213d]">🎉 Listen to Jessica share her wins 🎉</h3>
              <div className="relative rounded-xl overflow-hidden shadow-xl bg-black">
                <iframe 
                  src="https://www.loom.com/embed/d5e6c4167d2b46159c05c22f8c274c46?sid=3e3dcc67-e070-4929-8533-74dc489b949d&speed=1" 
                  frameBorder="0" 
                  allowFullScreen 
                  className="w-full h-64 md:h-96"
                ></iframe>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
            <div className="bg-[#08213d] text-white rounded-2xl p-8 flex items-start gap-6 h-full shadow-lg">
              <img src="https://i.imgur.com/pw0HIbP.jpg" className="w-20 h-20 rounded-full object-cover shrink-0" alt="David" />
              <div>
                <blockquote className="italic mb-6 text-gray-300">"Uriah's program helped me add far more strategy to my AI usage. The hands-on practice has been great."</blockquote>
                <p className="font-bold text-[#0EBA8E]">David Khalili, LPC</p>
              </div>
            </div>
            <div className="bg-[#08213d] text-white rounded-2xl p-8 flex items-start gap-6 h-full shadow-lg">
              <img src="https://i.imgur.com/qkfh4Eo.jpg" className="w-20 h-20 rounded-full object-cover shrink-0" alt="Sara" />
              <div>
                <blockquote className="italic mb-6 text-gray-300">"Before Uriah's mastermind, I had no idea how to actually integrate it. Now Claude is my executive co-pilot."</blockquote>
                <p className="font-bold text-[#0EBA8E]">Sara Weber, LPC</p>
              </div>
            </div>
            <div className="bg-[#08213d] text-white rounded-2xl p-8 flex items-start gap-6 h-full shadow-lg">
              <img src="https://i.imgur.com/BXSNnmq.jpg" className="w-20 h-20 rounded-full object-cover shrink-0" alt="Nichole" />
              <div>
                <blockquote className="italic mb-6 text-gray-300">"This mastermind revolutionized how I solve problems and lead my team. I use the strategies I learned every single day."</blockquote>
                <p className="font-bold text-[#0EBA8E]">Nichole Fusco, LICSW</p>
              </div>
            </div>
          </div>

          <ApplyButton variant="secondary" />
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="relative py-32 text-center overflow-hidden bg-[#08213d]">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1506377247377-2a5b3b0ca3ef?q=80&w=2000&auto=format&fit=crop")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-[#08213d]/75 backdrop-blur-[2px]"></div>
        </div>

        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-16 text-white drop-shadow-xl">Not Just Learning. Building Together.</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {differentiators.map((item, idx) => (
                <div key={idx} className="bg-[#08213d]/80 backdrop-blur-xl p-10 rounded-3xl border border-white/20 hover:border-[#0EBA8E]/50 transition-all duration-300 group text-left shadow-2xl h-full">
                  <div className="w-14 h-14 bg-[#0EBA8E]/20 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-[#0EBA8E]/30 transition-colors">
                    <SafeIcon icon={item.icon} className="w-7 h-7 text-[#0EBA8E]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-6 text-white">{item.title}</h3>
                  <p className="text-gray-200 leading-relaxed text-lg">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Wine Country Retreat Section */}
      <section className="py-24 bg-white text-[#08213d]">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#1B80C9]">The In-Person Experience</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Included in your mastermind is a restorative retreat in Northern California's wine country, right when you need it most. 
              <br /><br />
              We'll balance deep strategic thinking with the best local experiences, and honestly, meeting the people you've been working with for three months in real life? That's when the magic happens.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="group">
                <div className="overflow-hidden rounded-3xl shadow-2xl mb-8 aspect-video">
                  <img 
                    src="https://i.imgur.com/EDhMkZp.jpg" 
                    alt="Armstrong Redwoods" 
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#08213d] mb-4">Ancient Redwood Walk</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Find clarity among the giants. We'll take a peaceful walk through the Armstrong Redwoods State Natural Reserve, providing the perfect environment for rejuvenation and deep professional reflection.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="group">
                <div className="overflow-hidden rounded-3xl shadow-2xl mb-8 aspect-video">
                  <img 
                    src="https://i.imgur.com/YCGxHMh.jpg" 
                    alt="Wine tasting in Sonoma County" 
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#08213d] mb-4">Sonoma County Wine Tasting</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Connect with your fellow practice owners over a curated tasting experience. We'll visit hand-picked wineries to relax, share insights, and enjoy the incredible hospitality of the Sonoma region.
                </p>
              </div>
            </motion.div>
          </div>

          <ApplyButton variant="secondary" />
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-24 bg-[#e3f1fc] text-center text-[#08213d] border-t border-[#1B80C9]/10">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1B80C9] mb-16">
            <span className="block mb-6">Stop Going It Alone.</span>
            <span className="block text-[#0EBA8E]">Let's Build Your AI-Powered Practice.</span>
          </h2>
          
          <div className="max-w-2xl mx-auto bg-white p-8 md:p-10 rounded-3xl border border-[#1B80C9]/10 text-left shadow-lg">
            <h4 className="text-2xl font-bold mb-8 text-[#08213d]">
              What's Included:
            </h4>
            <ul className="grid gap-6">
              {whatsIncludedItems.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#0EBA8E] mt-1 shrink-0" />
                  <span className="text-gray-700 text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Your Investment Section */}
      <section className="py-24 bg-[#08213d] text-center text-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl md:text-5xl font-bold mb-16">Your Investment</h3>
            <div className="mb-12">
              <p className="text-7xl md:text-8xl font-black mb-6 text-[#0EBA8E] tracking-tight">$750<span className="text-2xl font-bold text-gray-400">/month</span></p>
              <div className="space-y-2">
                <p className="text-2xl md:text-3xl text-white font-bold">Early Bird Pricing</p>
                <p className="text-lg md:text-xl text-gray-300 font-medium">Increases to $950/month on Monday, January 19th</p>
              </div>
            </div>
            <div className="mb-12">
              <p className="text-lg text-[#08213d] font-bold py-3 px-8 bg-white inline-block rounded-full shadow-lg">
                Limited to 10 practice owners
              </p>
            </div>
            <div className="text-center max-w-3xl mx-auto bg-white p-10 md:p-14 rounded-[2.5rem] border border-[#1B80C9]/20 shadow-2xl transform hover:scale-[1.02] transition-transform duration-300">
              <div className="text-xl md:text-2xl text-gray-700 font-semibold leading-relaxed mb-10">
                Calls are on the 2nd and 4th Tuesdays each month <br className="hidden md:block" />
                <span className="text-[#1B80C9] font-bold">10–11am PST</span>
              </div>
              <p className="text-2xl md:text-3xl text-[#08213d] font-black uppercase tracking-tight">
                Mastermind starts <span className="text-[#0EBA8E]">Feb 10th</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50 text-[#08213d]">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-white text-[#08213d]">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#08213d] mb-6">Ready to Scale with AI?</h2>
            <p className="text-xl text-gray-600 mb-12">
              Secure your spot in the 2026 cohort. Remaining: <span className="text-[#0EBA8E] font-bold">Only 10 Spots</span>
            </p>
            <ApplyButton />
          </motion.div>
        </div>
      </section>

      <footer className="bg-[#08213d] py-12 text-center text-gray-400 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="mb-6">
            <img src="https://greta-preview.s3.us-east-2.amazonaws.com/assets/logo.svg" alt="Productive Therapist Logo" className="h-10 mx-auto grayscale opacity-50 mb-4" />
          </div>
          <p className="text-sm">© 2026 Productive Therapist. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;